import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Chip, IconButton, Button, Grid, Paper, Divider,
  Checkbox, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Rating, Snackbar, Alert,
  List, ListItem, ListItemText, Skeleton, Link, Tooltip,
} from '@mui/material';
import {
  Star, StarBorder, Edit, Delete, AccessTime, People, ArrowBack,
  CheckCircle, Bookmark, Add, Launch, MenuBook, ContentCopy,
} from '@mui/icons-material';
import { getRecipe, updateRecipe, deleteRecipe, createRecipe } from '../../utils/firebase';
import { useRecipes } from '../../contexts/RecipeContext';
import { useAuth } from '../../contexts/AuthContext';
import { CATEGORIES, CATEGORY_GRADIENTS, DIFFICULTY } from '../../config';
import CookingModeDialog from './CookingModeDialog';

function scaleAmount(amountStr, scale) {
  if (!amountStr || scale === 1) return amountStr;
  // Handle fractions like "1/2", "1 1/2"
  const parse = (s) => {
    const mixed = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixed) return parseInt(mixed[1]) + parseInt(mixed[2]) / parseInt(mixed[3]);
    const frac = s.match(/^(\d+)\/(\d+)$/);
    if (frac) return parseInt(frac[1]) / parseInt(frac[2]);
    return parseFloat(s);
  };
  const val = parse(String(amountStr).trim());
  if (isNaN(val)) return amountStr;
  const scaled = val * scale;
  // Format nicely
  if (Number.isInteger(scaled)) return String(scaled);
  return String(Math.round(scaled * 100) / 100);
}

export default function RecipeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pantry } = useRecipes();
  const { user } = useAuth();

  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedIngredients, setCheckedIngredients] = useState({});
  const [madeDialogOpen, setMadeDialogOpen] = useState(false);
  const [madeRating, setMadeRating] = useState(0);
  const [madeNote, setMadeNote] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [cookingMode, setCookingMode] = useState(false);
  const [servingScale, setServingScale] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadRecipe(); // eslint-disable-line react-hooks/exhaustive-deps
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRecipe = async () => {
    try {
      setLoading(true);
      const data = await getRecipe(id);
      if (!data) {
        navigate('/recipes');
        return;
      }
      setRecipe(data);
      setNotes(data.notes || '');
    } catch (err) {
      showSnackbar('Failed to load recipe', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleStarToggle = async () => {
    try {
      await updateRecipe(id, { starred: !recipe.starred });
      setRecipe((prev) => ({ ...prev, starred: !prev.starred }));
    } catch (err) {
      showSnackbar('Failed to update', 'error');
    }
  };

  const handleMarkMade = async () => {
    try {
      await updateRecipe(id, {
        status: 'made',
        rating: madeRating || null,
        notes: madeNote ? `${recipe.notes || ''}\n${madeNote}`.trim() : recipe.notes,
      });
      setRecipe((prev) => ({ ...prev, status: 'made', rating: madeRating || null }));
      setMadeDialogOpen(false);
      setMadeRating(0);
      setMadeNote('');
      showSnackbar('Marked as made!');
    } catch (err) {
      showSnackbar('Failed to update', 'error');
    }
  };

  const handleMarkBookmarked = async () => {
    try {
      await updateRecipe(id, { status: 'bookmarked', rating: null });
      setRecipe((prev) => ({ ...prev, status: 'bookmarked', rating: null }));
      showSnackbar('Moved back to bookmarked');
    } catch (err) {
      showSnackbar('Failed to update', 'error');
    }
  };

  const handleDuplicate = async () => {
    try {
      const { id: _id, userId: _uid, createdAt: _ca, updatedAt: _ua, ...rest } = recipe;
      const newId = await createRecipe(user.uid, { ...rest, title: `${recipe.title} (copy)`, status: 'bookmarked' });
      showSnackbar('Recipe duplicated!');
      navigate(`/recipes/${newId}`);
    } catch (err) {
      showSnackbar('Failed to duplicate', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRecipe(id);
      navigate('/recipes');
    } catch (err) {
      showSnackbar('Failed to delete', 'error');
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      await updateRecipe(id, { notes });
      setRecipe((prev) => ({ ...prev, notes }));
      showSnackbar('Notes saved');
    } catch (err) {
      showSnackbar('Failed to save notes', 'error');
    } finally {
      setSavingNotes(false);
    }
  };

  const isInPantry = (ingredientName) => {
    const name = ingredientName?.toLowerCase() || '';
    return pantry.items?.some(
      (item) =>
        item.inStock &&
        (item.name.toLowerCase().includes(name) || name.includes(item.name.toLowerCase()))
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rounded" height={300} sx={{ mb: 2 }} />
        <Skeleton variant="text" height={50} width="60%" />
        <Skeleton variant="text" height={25} width="40%" />
      </Box>
    );
  }

  if (!recipe) return null;

  const category = CATEGORIES.find((c) => c.value === recipe.category);
  const difficulty = DIFFICULTY.find((d) => d.value === recipe.difficulty);
  const gradient = CATEGORY_GRADIENTS[recipe.category] || CATEGORY_GRADIENTS.dinner;

  const hasPantryItems = pantry.items?.some((i) => i.inStock);
  const missingIngredients = recipe.ingredients?.filter((ing) => !isInPantry(ing.name)) || [];

  const handleAddMissingToPantry = async () => {
    const newItems = [
      ...(pantry.items || []),
      ...missingIngredients
        .filter((ing) => !pantry.items?.some((p) => p.name.toLowerCase() === ing.name.toLowerCase()))
        .map((ing) => ({ id: Date.now() + Math.random(), name: ing.name, inStock: false })),
    ];
    try {
      const { savePantry } = await import('../../utils/firebase');
      await savePantry(recipe.userId, newItems);
      showSnackbar('Missing ingredients added to pantry');
    } catch (err) {
      showSnackbar('Failed to update pantry', 'error');
    }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Hero */}
      <Box
        sx={{
          height: { xs: 220, md: 320 },
          background: recipe.coverImage ? `url(${recipe.coverImage}) center/cover` : gradient,
          position: 'relative',
        }}
      >
        {/* Back button */}
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ position: 'absolute', top: 12, left: 12, bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
        >
          <ArrowBack />
        </IconButton>

        {/* Actions */}
        <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 1 }}>
          <Tooltip title="Cook mode">
            <IconButton
              onClick={() => setCookingMode(true)}
              sx={{ bgcolor: 'rgba(240,135,60,0.7)', backdropFilter: 'blur(4px)', color: '#fff' }}
            >
              <MenuBook />
            </IconButton>
          </Tooltip>
          <Tooltip title={recipe.starred ? 'Unstar' : 'Star'}>
            <IconButton
              onClick={handleStarToggle}
              sx={{ bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', color: recipe.starred ? 'warning.main' : '#fff' }}
            >
              {recipe.starred ? <Star /> : <StarBorder />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate">
            <IconButton
              onClick={handleDuplicate}
              sx={{ bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
            >
              <ContentCopy />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              onClick={() => navigate(`/recipes/${id}/edit`)}
              sx={{ bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', color: 'error.main' }}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Status badge */}
        <Chip
          label={recipe.status === 'made' ? '✅ Made It' : '📌 Bookmarked'}
          sx={{ position: 'absolute', bottom: 16, left: 16, bgcolor: 'rgba(0,0,0,0.7)', color: '#fff', backdropFilter: 'blur(4px)' }}
        />
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Title + category */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
            <Typography variant="h4" sx={{ flex: 1 }}>{recipe.title}</Typography>
          </Box>
          {category && (
            <Chip
              label={`${category.emoji} ${category.label}`}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
            />
          )}
          {recipe.description && (
            <Typography color="text.secondary" sx={{ mt: 1.5 }}>
              {recipe.description}
            </Typography>
          )}
        </Box>

        {/* Quick stats */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          {recipe.prepTime > 0 && (
            <Paper sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime sx={{ fontSize: 18, color: 'primary.main' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Prep</Typography>
                <Typography variant="body2" fontWeight={600}>{recipe.prepTime}m</Typography>
              </Box>
            </Paper>
          )}
          {recipe.cookTime > 0 && (
            <Paper sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime sx={{ fontSize: 18, color: 'warning.main' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Cook</Typography>
                <Typography variant="body2" fontWeight={600}>{recipe.cookTime}m</Typography>
              </Box>
            </Paper>
          )}
          {recipe.servings && (
            <Paper sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <People sx={{ fontSize: 18, color: 'secondary.main' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Serves</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton size="small" onClick={() => setServingScale((s) => Math.max(0.5, s - 0.5))}
                    sx={{ p: 0.25, opacity: 0.6, '&:hover': { opacity: 1 } }}>
                    <Typography sx={{ fontSize: 14, lineHeight: 1, fontWeight: 700 }}>−</Typography>
                  </IconButton>
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 28, textAlign: 'center' }}>
                    {Math.round(recipe.servings * servingScale)}
                  </Typography>
                  <IconButton size="small" onClick={() => setServingScale((s) => s + 0.5)}
                    sx={{ p: 0.25, opacity: 0.6, '&:hover': { opacity: 1 } }}>
                    <Typography sx={{ fontSize: 14, lineHeight: 1, fontWeight: 700 }}>+</Typography>
                  </IconButton>
                </Box>
              </Box>
              {servingScale !== 1 && (
                <Chip label={`${servingScale}×`} size="small"
                  onClick={() => setServingScale(1)}
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(240,135,60,0.15)', color: 'primary.main',
                    cursor: 'pointer', border: '1px solid rgba(240,135,60,0.3)' }} />
              )}
            </Paper>
          )}
          {difficulty && (
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary">Difficulty</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: difficulty.color }}>
                {difficulty.label}
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Tags */}
        {recipe.tags?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            {recipe.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.07)' }} />
            ))}
          </Box>
        )}

        {/* Rating */}
        {recipe.status === 'made' && recipe.rating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Your rating:</Typography>
            <Rating value={recipe.rating} readOnly />
          </Box>
        )}

        <Divider sx={{ my: 2, opacity: 0.1 }} />

        {/* Two-column: Ingredients + Instructions */}
        <Grid container spacing={3}>
          {/* Ingredients */}
          <Grid item xs={12} md={5}>
            <Typography variant="h6" sx={{ mb: 2 }}>🧾 Ingredients</Typography>
            {recipe.ingredients?.length > 0 ? (
              <List dense>
                {recipe.ingredients.map((ing, i) => {
                  const inStock = isInPantry(ing.name);
                  return (
                    <ListItem key={ing.id || i} disablePadding sx={{ py: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={!!checkedIngredients[i]}
                        onChange={() =>
                          setCheckedIngredients((prev) => ({ ...prev, [i]: !prev[i] }))
                        }
                        sx={{ p: 0.5, mr: 1 }}
                      />
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              textDecoration: checkedIngredients[i] ? 'line-through' : 'none',
                              opacity: checkedIngredients[i] ? 0.5 : 1,
                              color: !hasPantryItems ? 'text.primary' : inStock ? 'success.main' : 'text.primary',
                            }}
                          >
                            {[scaleAmount(ing.amount, servingScale), ing.unit, ing.name].filter(Boolean).join(' ')}
                          </Typography>
                        }
                      />
                      {hasPantryItems && (
                        <Typography
                          variant="caption"
                          sx={{ ml: 1, flexShrink: 0, color: inStock ? 'success.main' : 'text.secondary' }}
                        >
                          {inStock ? '✅' : '❌'}
                        </Typography>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Typography color="text.secondary">No ingredients listed.</Typography>
            )}

            {/* Shopping list */}
            {hasPantryItems && missingIngredients.length > 0 && (
              <Paper sx={{ mt: 2, p: 2, bgcolor: 'rgba(255,255,255,0.03)' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>🛒 Shopping List</Typography>
                {missingIngredients.map((ing, i) => (
                  <Typography key={i} variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
                    • {[scaleAmount(ing.amount, servingScale), ing.unit, ing.name].filter(Boolean).join(' ')}
                  </Typography>
                ))}
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddMissingToPantry}
                  sx={{ mt: 1, color: 'primary.main' }}
                >
                  Add missing to pantry
                </Button>
              </Paper>
            )}
          </Grid>

          {/* Instructions */}
          <Grid item xs={12} md={7}>
            <Typography variant="h6" sx={{ mb: 2 }}>📋 Instructions</Typography>
            {recipe.instructions?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {recipe.instructions.map((inst, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                    <Box
                      sx={{
                        width: 32, height: 32, borderRadius: '50%',
                        bgcolor: 'primary.dark', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, fontWeight: 700, fontSize: '0.85rem',
                      }}
                    >
                      {inst.step || i + 1}
                    </Box>
                    <Typography variant="body2" sx={{ pt: 0.5, lineHeight: 1.7 }}>
                      {inst.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary">No instructions listed.</Typography>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, opacity: 0.1 }} />

        {/* Notes */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>📝 My Notes</Typography>
          <TextField
            multiline
            rows={5}
            fullWidth
            placeholder="Add your personal notes, variations, tips..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={handleSaveNotes}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Button
            size="small"
            onClick={handleSaveNotes}
            disabled={savingNotes}
            sx={{ mt: 1, color: 'primary.main' }}
          >
            {savingNotes ? 'Saving...' : 'Save Notes'}
          </Button>
        </Box>

        {/* Source URL */}
        {recipe.sourceUrl && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Source:{' '}
              <Link href={recipe.sourceUrl} target="_blank" rel="noopener" sx={{ color: 'primary.main' }}>
                {recipe.sourceUrl} <Launch sx={{ fontSize: 12, verticalAlign: 'middle' }} />
              </Link>
            </Typography>
          </Box>
        )}

        {/* Photos */}
        {recipe.photos?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>📷 Photos</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {recipe.photos.map((url, i) => (
                <Box
                  key={i}
                  component="img"
                  src={url}
                  alt={`Photo ${i + 1}`}
                  sx={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 2 }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* CTA Buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {recipe.status === 'bookmarked' && (
            <Button
              variant="contained"
              startIcon={<CheckCircle />}
              onClick={() => setMadeDialogOpen(true)}
              sx={{ background: 'linear-gradient(135deg, #6bb07a, #4a8a58)' }}
            >
              Mark as Made
            </Button>
          )}
          {recipe.status === 'made' && (
            <Button
              variant="outlined"
              startIcon={<Bookmark />}
              onClick={handleMarkBookmarked}
              sx={{ borderColor: 'rgba(255,255,255,0.2)' }}
            >
              Move to Bookmarked
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => navigate(`/recipes/${id}/edit`)}
            sx={{ borderColor: 'rgba(255,255,255,0.2)' }}
          >
            Edit Recipe
          </Button>
        </Box>
      </Box>

      {/* Cooking Mode */}
      <CookingModeDialog open={cookingMode} onClose={() => setCookingMode(false)} recipe={recipe} />

      {/* Mark as Made Dialog */}
      <Dialog open={madeDialogOpen} onClose={() => setMadeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark as Made 🍽️</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            How did it turn out?
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2">Rating:</Typography>
            <Rating value={madeRating} onChange={(_, v) => setMadeRating(v)} />
          </Box>
          <TextField
            label="Quick note (optional)"
            multiline
            rows={2}
            fullWidth
            value={madeNote}
            onChange={(e) => setMadeNote(e.target.value)}
            placeholder="How did it go? Any variations?"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMadeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMarkMade} sx={{ background: 'linear-gradient(135deg, #6bb07a, #4a8a58)' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Recipe?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete "{recipe.title}"? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
